import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Sparkles, Snowflake, Heart, Leaf, Clock, ArrowRight, MapPin, ChevronRight, ChevronLeft, ExternalLink, Facebook, MessageCircle, Instagram, Play, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import ProductCard from '../components/ProductCard';
import { categories } from '../data/products';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/940e8ae3-c19e-4add-8a26-04b3a3541f3d/images/7d130a96492d7f658f6aeed255da2974552d7dc094b2c752983adafbdf294bb2.png";
const TEXTURE_IMG = "https://static.prod-images.emergentagent.com/jobs/940e8ae3-c19e-4add-8a26-04b3a3541f3d/images/229ae3bfa832c07916b758ef5a0a9b91f5df3daade1f9def80d6981d05323936.png";
const STORE_IMG = "https://images.unsplash.com/photo-1755409022211-93efd6ecae73?w=800";

const iconMap = { sparkles: Sparkles, snowflake: Snowflake, heart: Heart, leaf: Leaf, clock: Clock };

const faqs = [
  { q: "Como hago un pedido?", a: "Selecciona tu torta favorita, personaliza el tamano, relleno y sabor, y finaliza tu compra con un deposito del 50%. Recibiras confirmacion por WhatsApp." },
  { q: "Cuanto tiempo de anticipacion necesito?", a: "Depende del producto. Las tortas especiales requieren minimo 2-3 dias. Los productos 'Pide para Hoy' estan disponibles de inmediato." },
  { q: "Hacen entregas a domicilio?", a: "Si, realizamos entregas en Medellín y el Área Metropolitana. El costo de envio se calcula segun la ubicacion." },
  { q: "Puedo personalizar completamente mi torta?", a: "Claro! Puedes elegir tamano, masa, relleno, sabor y complementos. Tambien puedes agregar observaciones especiales." },
  { q: "Que metodos de pago aceptan?", a: "Aceptamos transferencias bancarias, pagos con tarjeta y pago contra entrega en algunos casos. Se requiere un deposito del 50% para confirmar el pedido." },
  { q: "Puedo cancelar o modificar mi pedido?", a: "Puedes modificar tu pedido hasta 24 horas antes de la fecha de entrega contactandonos por WhatsApp." },
];

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [stores, setStores] = useState([]);
  const [publicSettings, setPublicSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const [featuredRes, popularRes, storesRes, settingsRes] = await Promise.all([
          axios.get(`${API}/products?featured=true`),
          axios.get(`${API}/products?popular=true`),
          axios.get(`${API}/stores`),
          axios.get(`${API}/settings/public`),
        ]);
        setFeatured(featuredRes.data);
        setPopular(popularRes.data);
        setStores(storesRes.data);
        setPublicSettings(settingsRes.data || {});
      } catch (err) {
        console.error("Error loading homepage data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHomeData();
  }, []);

  const activeBanners = publicSettings?.banners && publicSettings.banners.length > 0
    ? publicSettings.banners
    : (publicSettings?.banner_images && publicSettings.banner_images.length > 0
        ? publicSettings.banner_images.map(img => ({
            image: img,
            type: 'legacy'
          }))
        : [{
            image: "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=600",
            type: 'legacy'
          }]
      );

  useEffect(() => {
    if (activeBanners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % activeBanners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeBanners.length]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/productos?search=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <div className="animate-fade-in space-y-8 pb-12">
      {/* DRIP BANNER HERO */}
      <section className="relative w-full bg-[#f6ddf1] overflow-hidden min-h-[500px]" data-testid="hero-section">
        {/* Beautiful Drip Chocolate effect on top */}
        <div className="absolute top-0 left-0 right-0 h-10 bg-[#4e3629] z-30 rounded-b-[40px] shadow-md flex items-center justify-between px-10">
          <div className="w-4 h-8 bg-[#4e3629] rounded-b-full"></div>
          <div className="w-6 h-12 bg-[#4e3629] rounded-b-full -mt-2"></div>
          <div className="w-5 h-10 bg-[#4e3629] rounded-b-full -mt-1"></div>
          <div className="w-8 h-14 bg-[#4e3629] rounded-b-full -mt-3"></div>
          <div className="w-4 h-7 bg-[#4e3629] rounded-b-full"></div>
          <div className="w-7 h-11 bg-[#4e3629] rounded-b-full -mt-2"></div>
          <div className="w-3 h-6 bg-[#4e3629] rounded-b-full"></div>
          <div className="w-6 h-12 bg-[#4e3629] rounded-b-full -mt-2"></div>
          <div className="w-4 h-9 bg-[#4e3629] rounded-b-full -mt-1"></div>
          <div className="w-9 h-16 bg-[#4e3629] rounded-b-full -mt-4"></div>
          <div className="w-5 h-10 bg-[#4e3629] rounded-b-full -mt-1"></div>
          <div className="w-7 h-13 bg-[#4e3629] rounded-b-full -mt-3"></div>
          <div className="w-4 h-8 bg-[#4e3629] rounded-b-full"></div>
        </div>

        {/* Banners Slider Container */}
        <div className="relative w-full min-h-[500px] flex items-center">
          {activeBanners.map((banner, idx) => {
            const isActive = currentSlide === idx;
            return (
              <div
                key={idx}
                className={`absolute inset-0 w-full h-full flex items-center transition-all duration-1000 ${
                  isActive ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'
                }`}
              >
                {banner.is_jumbotron ? (
                  /* Jumbotron layout: 100% background image with text/CTA overlaid */
                  <div className="absolute inset-0 w-full h-full flex items-center justify-center">
                    <img
                      src={banner.image}
                      alt={banner.title || 'Banner'}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/45 backdrop-blur-[1px] flex items-center justify-center">
                      <div className="max-w-4xl mx-auto px-6 text-center text-white space-y-6 pt-8 animate-fade-in">
                        {banner.title && (
                          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold leading-tight drop-shadow-md text-[#fff0f5]">
                            {banner.title}
                          </h1>
                        )}
                        {banner.subtitle && (
                          <p className="font-body text-sm sm:text-base md:text-lg text-white/95 max-w-2xl mx-auto drop-shadow-sm leading-relaxed">
                            {banner.subtitle}
                          </p>
                        )}
                        {banner.cta_text && (
                          <div className="flex justify-center pt-2 gap-3">
                            {banner.cta_link === 'video' ? (
                              <button
                                type="button"
                                onClick={() => setIsVideoOpen(true)}
                                className="flex items-center gap-2 bg-[#d63384] hover:bg-[#b8226b] text-white px-6 py-3 rounded-full font-body text-xs font-bold transition-all shadow-lg"
                              >
                                <HelpCircle className="w-4 h-4" /> {banner.cta_text}
                              </button>
                            ) : (
                              <Link
                                to={banner.cta_link || '/productos'}
                                className="flex items-center gap-2 bg-[#d63384] hover:bg-[#b8226b] text-white px-6 py-3 rounded-full font-body text-xs font-bold transition-all shadow-lg"
                              >
                                {banner.cta_text} &rarr;
                              </Link>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Split Layout: 50% Text, 50% Image */
                  <div className="max-w-6xl mx-auto px-4 py-24 flex flex-col md:flex-row items-center justify-between gap-8 w-full">
                    {/* Left text column */}
                    <div className="flex-1 text-center md:text-left space-y-4">
                      {banner.type === 'legacy' ? (
                        <>
                          <div className="inline-block bg-[#fff0f5] text-[#d63384] px-4 py-1.5 rounded-full font-body text-xs font-bold uppercase tracking-wider shadow-sm animate-pulse">
                            ✨ Pide ahora una torta especial
                          </div>
                          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-[#4a2e1d] leading-tight">
                            Pedir un <span className="text-[#d63384]">Deseo</span> no es lo mismo sin una torta
                          </h1>
                          <p className="font-body text-sm text-[#5c4033]/80 max-w-md">
                            Tortas artesanales hechas con amor y los mejores ingredientes. Personaliza tu torta perfecta para hacer tus momentos inolvidables.
                          </p>
                          <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                            <button
                              type="button"
                              onClick={() => setIsVideoOpen(true)}
                              className="flex items-center gap-2 bg-[#d63384] hover:bg-[#b8226b] text-white px-5 py-2.5 rounded-full font-body text-xs font-bold transition-all shadow-md"
                            >
                              <HelpCircle className="w-4 h-4" /> ¿Cómo hago un pedido?
                            </button>
                            <Link
                              to="/productos"
                              className="flex items-center gap-2 bg-white hover:bg-gray-50 text-[#4a2e1d] px-5 py-2.5 rounded-full font-body text-xs font-bold border border-[#e5dfd3] transition-all shadow-sm"
                            >
                              Ver Productos &rarr;
                            </Link>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="inline-block bg-[#fff0f5] text-[#d63384] px-4 py-1.5 rounded-full font-body text-xs font-bold uppercase tracking-wider shadow-sm">
                            ✨ ¡Keiks Repostería!
                          </div>
                          {banner.title && (
                            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-[#4a2e1d] leading-tight">
                              {banner.title}
                            </h1>
                          )}
                          {banner.subtitle && (
                            <p className="font-body text-sm text-[#5c4033]/80 max-w-md">
                              {banner.subtitle}
                            </p>
                          )}
                          {banner.cta_text && (
                            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                              {banner.cta_link === 'video' ? (
                                <button
                                  type="button"
                                  onClick={() => setIsVideoOpen(true)}
                                  className="flex items-center gap-2 bg-[#d63384] hover:bg-[#b8226b] text-white px-5 py-2.5 rounded-full font-body text-xs font-bold transition-all shadow-md"
                                >
                                  <HelpCircle className="w-4 h-4" /> {banner.cta_text}
                                </button>
                              ) : (
                                <Link
                                  to={banner.cta_link || '/productos'}
                                  className="flex items-center gap-2 bg-[#d63384] hover:bg-[#b8226b] text-white px-5 py-2.5 rounded-full font-body text-xs font-bold transition-all shadow-md"
                                >
                                  {banner.cta_text} &rarr;
                                </Link>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Right image column */}
                    <div className="flex-1 max-w-sm md:max-w-md relative">
                      <div className="relative overflow-hidden rounded-3xl shadow-2xl border-4 border-white aspect-square">
                        <img
                          src={banner.image}
                          alt={banner.title || 'Banner Keiks'}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </div>

                      {/* 100% Artesanal Badge */}
                      <div className="absolute -bottom-4 -left-4 bg-white/90 backdrop-blur-sm p-4 rounded-2xl shadow-lg border border-[#e5dfd3] flex items-center gap-3 z-20">
                        <div className="bg-[#a0d5ba] text-[#46371c] p-2 rounded-xl">
                          🎂
                        </div>
                        <div>
                          <p className="font-heading text-sm font-bold text-[#46371c]">100% Artesanal</p>
                          <p className="font-body text-[10px] text-[#75664d]">Hecho con mucho amor</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Carousel controls */}
        {activeBanners.length > 1 && (
          <>
            <button
              onClick={() => setCurrentSlide(prev => (prev - 1 + activeBanners.length) % activeBanners.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-[#4a2e1d] p-2 rounded-full z-20 shadow-md transition-all"
              type="button"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentSlide(prev => (prev + 1) % activeBanners.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-[#4a2e1d] p-2 rounded-full z-20 shadow-md transition-all"
              type="button"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20 bg-black/20 backdrop-blur-sm px-3 py-2 rounded-full">
              {activeBanners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentSlide === idx ? 'bg-white w-4' : 'bg-white/50'
                  }`}
                  type="button"
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* CATEGORIES CIRCULAR LAYOUT */}
      <section className="max-w-6xl mx-auto px-4 py-8" data-testid="categories-section">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-6 justify-center">
          {/* Tortas Especiales */}
          <Link to="/productos?category=tortas-especiales" className="flex flex-col items-center gap-2 group">
            <div className="w-16 h-16 rounded-full bg-[#fbc6ad] hover:bg-[#fba077] transition-all flex items-center justify-center shadow-md group-hover:scale-105">
              <span className="text-2xl">🎂</span>
            </div>
            <span className="font-body text-xs font-semibold text-[#46371c] text-center group-hover:text-[#a0d5ba]">Tortas Especiales</span>
          </Link>

          {/* Tortas Refrigeradas */}
          <Link to="/productos?category=refrigeradas" className="flex flex-col items-center gap-2 group">
            <div className="w-16 h-16 rounded-full bg-[#addcf7] hover:bg-[#7bc4f4] transition-all flex items-center justify-center shadow-md group-hover:scale-105">
              <span className="text-2xl">❄️</span>
            </div>
            <span className="font-body text-xs font-semibold text-[#46371c] text-center group-hover:text-[#a0d5ba]">Tortas Refrigeradas</span>
          </Link>

          {/* Tortas Clasicas */}
          <Link to="/productos?category=clasicas" className="flex flex-col items-center gap-2 group">
            <div className="w-16 h-16 rounded-full bg-[#fbd4d6] hover:bg-[#fb9da2] transition-all flex items-center justify-center shadow-md group-hover:scale-105">
              <span className="text-2xl">🍰</span>
            </div>
            <span className="font-body text-xs font-semibold text-[#46371c] text-center group-hover:text-[#a0d5ba]">Tortas Clasicas</span>
          </Link>

          {/* Tortas Saludables */}
          <Link to="/productos?category=saludables" className="flex flex-col items-center gap-2 group">
            <div className="w-16 h-16 rounded-full bg-[#fdf2b3] hover:bg-[#fdec7b] transition-all flex items-center justify-center shadow-md group-hover:scale-105">
              <span className="text-2xl">🧁</span>
            </div>
            <span className="font-body text-xs font-semibold text-[#46371c] text-center group-hover:text-[#a0d5ba]">Tortas Saludables</span>
          </Link>

          {/* Pide Para Hoy */}
          <Link to="/productos?category=pide-para-hoy" className="flex flex-col items-center gap-2 group">
            <div className="w-16 h-16 rounded-full bg-[#dec9f9] hover:bg-[#c29efa] transition-all flex items-center justify-center shadow-md group-hover:scale-105">
              <span className="text-2xl">⏰</span>
            </div>
            <span className="font-body text-xs font-semibold text-[#46371c] text-center group-hover:text-[#a0d5ba]">Pide Para Hoy</span>
          </Link>

          {/* Piñatería */}
          <Link to="/productos?category=pinateria" className="flex flex-col items-center gap-2 group">
            <div className="w-16 h-16 rounded-full bg-[#fde2e4] hover:bg-[#ffb5a7] transition-all flex items-center justify-center shadow-md group-hover:scale-105">
              <span className="text-2xl">🎉</span>
            </div>
            <span className="font-body text-xs font-semibold text-[#46371c] text-center group-hover:text-[#a0d5ba]">Piñatería</span>
          </Link>
        </div>
      </section>

      {/* POPULAR / LOS MAS VENDIDOS */}
      <section className="py-8" data-testid="featured-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#e05275]">Los Mas Vendidos</h2>
          </div>
          <div className="flex justify-between items-end mb-6">
            <div>
              <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#46371c]/60 mb-2">Selección Especial</p>
            </div>
            <Link to="/productos" className="hidden sm:flex items-center gap-1 font-body text-sm font-medium text-[#46371c] hover:text-[#a0d5ba] transition-colors" data-testid="view-all-featured">
              Ver todo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {featured.slice(0, 3).map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* POPULAR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="popular-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#a0d5ba] mb-2">Los Favoritos</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[#46371c]">Mas Populares</h2>
          </div>
          <Link to="/productos" className="hidden sm:flex items-center gap-1 font-body text-sm font-medium text-[#46371c] hover:text-[#a0d5ba] transition-colors" data-testid="view-all-popular">
            Ver todo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {popular.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* EVENTS */}
      <section id="eventos" className="bg-[#a0d5ba] py-16 sm:py-24" data-testid="events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#46371c]/60 mb-2">Celebra con Keiks</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[#46371c] mb-10">Eventos Especiales</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Primera Comunión", desc: "Tortas elegantes y delicadas para este momento tan especial", img: "https://images.unsplash.com/photo-1760401697752-ab723885ff27?w=400", link: "/productos?category=comunion" },
              { title: "Grados y Graduaciones", desc: "Celebra tus logros con una torta personalizada", img: "https://images.unsplash.com/photo-1741865079571-6efd72a6cf92?w=400", link: "/productos?category=grados" },
              { title: "Cumpleaños", desc: "La torta perfecta para soplar las velitas", img: "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400", link: "/productos?category=tortas-especiales" },
            ].map((event, i) => (
              <Card key={i} className="overflow-hidden border-0 bg-white rounded-2xl group cursor-pointer" data-testid={`event-card-${i}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={event.img} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-2">{event.title}</h3>
                  <p className="font-body text-sm text-[#75664d] mb-4">{event.desc}</p>
                  <Link to={event.link} className="font-body text-sm font-medium text-[#46371c] flex items-center gap-1 hover:gap-2 transition-all">
                    Ver tortas <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* RAPPI BANNER */}
      {publicSettings.rappi_enabled && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="rappi-section">
          <div className="relative rounded-3xl overflow-hidden">
            <img src={TEXTURE_IMG} alt="" className="w-full h-48 sm:h-56 object-cover" />
            <div className="absolute inset-0 bg-[#46371c]/60 flex items-center justify-center">
              <div className="text-center px-4">
                <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#fcfbf9] mb-3">Pídenos por Rappi</h3>
                <p className="font-body text-sm text-[#fcfbf9]/80 mb-4">Selecciona tu sede más cercana abajo para pedir directamente</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {stores.filter(s => s.rappi_url).map(s => (
                    <a key={s.id} href={s.rappi_url} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-[#a0d5ba] text-[#46371c] hover:bg-[#82ba9e] rounded-full px-6 font-body text-xs font-semibold">
                        Rappi {s.name.replace('Keiks ', '')}
                      </Button>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* STORES */}
      <section id="tiendas" className="bg-[#f4f1ea] py-16 sm:py-24" data-testid="stores-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#a0d5ba] mb-2">Visitanos</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[#46371c] mb-10">Nuestras Tiendas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-1 md:col-span-3 flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : stores.length === 0 ? (
              <div className="col-span-1 md:col-span-3 text-center py-12 font-body text-[#75664d]">
                No hay tiendas disponibles en este momento.
              </div>
            ) : (
              stores.map((s, index) => (
                <Card key={s.id || index} className="overflow-hidden border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl flex flex-col justify-between" data-testid={`store-card-${s.id}`}>
                  <div>
                    <img src={s.image} alt={s.name} className="w-full h-56 object-cover" />
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-2">{s.name}</h3>
                      <p className="font-body text-sm text-[#75664d] flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 shrink-0" /> {s.address}
                      </p>
                      <p className="font-body text-sm text-[#75664d]">
                        {s.hours}
                      </p>
                    </div>
                  </div>
                  <div className="px-6 pb-6 pt-2 flex flex-col gap-2">
                    {s.rappi_url && (
                      <a href={s.rappi_url} target="_blank" rel="noopener noreferrer" className="w-full">
                        <Button className="w-full bg-[#eef6f2] hover:bg-[#e2f0e9] text-[#347a54] border border-[#a0d5ba] rounded-full font-body text-xs font-semibold" data-testid={`rappi-store-${s.id}`}>
                          Pedir por Rappi
                        </Button>
                      </a>
                    )}
                    {s.maps_url && (
                      <a href={s.maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#a0d5ba] hover:underline font-body font-semibold">
                        Ver en Google Maps <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#a0d5ba] mb-2 text-center">Resolvemos tus dudas</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[#46371c] mb-10 text-center">Preguntas Frecuentes</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-[#e5dfd3] rounded-xl px-4 bg-[#fcfbf9]" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="font-body text-sm font-medium text-[#46371c] hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="font-body text-sm text-[#75664d]">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Tutorial Video Dialog */}
      <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
        <DialogContent className="max-w-3xl bg-[#fcfbf9] border border-[#e5dfd3] p-6 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-[#46371c]">¿Cómo hago un pedido?</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#75664d]">
              Sigue los sencillos pasos de este video tutorial para realizar tu pedido de forma correcta.
            </DialogDescription>
          </DialogHeader>
          <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-[#e5dfd3] bg-black mt-2 shadow-inner">
            {isVideoOpen && (
              <iframe
                title="Tutorial de Pedido en Keiks"
                src="https://www.youtube.com/embed/LwUQqgnLUP4?autoplay=1"
                className="absolute inset-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
